import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { trpc } from "../lib/trpc.js";
import { duracao } from "../lib/formato.js";

/**
 * Espelho do dia: marcações, previsto e o que a apuração concluiu.
 * O número aparece em minutos formatados como "8h30", nunca em 8,5: o DP lê
 * assim, e float de horas acumula erro ao somar 220 dias.
 */
export function Ponto() {
  const [marcacoes, setMarcacoes] = useState("08:02, 12:00, 13:00, 18:35");
  const [dataRef, setDataRef] = useState("2026-08-14");

  // O motor trabalha em MINUTOS inteiros, nunca em float de horas: 8,5 acumula
  // erro ao somar 220 dias, e 510 não acumula nada.
  const emMinutos = (hhmm: string): number => {
    const [h, m] = hhmm.split(":").map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  const lista = marcacoes.split(",").map((s) => s.trim()).filter(Boolean).map(emMinutos);
  const apuracao = useQuery({
    queryKey: ["ponto", "apurar", dataRef, lista.join("-")],
    queryFn: () => trpc.ponto.apurarDia.query({
      dataRef, tipoDia: "util",
      marcacoes: lista,
      previsto: [{ inicio: emMinutos("08:00"), fim: emMinutos("17:00") }],
      intervalosPrevistos: [{ inicio: emMinutos("12:00"), fim: emMinutos("13:00") }],
    }),
    retry: false,
  });

  return (
    <Pagina
      secao="Jornada"
      titulo="Espelho do ponto"
      descricao="A apuração roda sobre a marcação registrada, sem alterar nada: correção é registro novo, nunca edição."
    >
      <div className="painel flex flex-wrap items-end gap-3 p-4">
        <label className="text-sm">
          <span className="titulo-secao block pb-1">Dia</span>
          <input className="campo w-40" type="date" value={dataRef}
            onChange={(e) => setDataRef(e.target.value)} aria-label="Dia" />
        </label>
        <label className="min-w-64 flex-1 text-sm">
          <span className="titulo-secao block pb-1">Marcações</span>
          <input className="campo" value={marcacoes}
            onChange={(e) => setMarcacoes(e.target.value)} aria-label="Marcações" />
        </label>
      </div>

      {apuracao.isPending && <Carregando />}
      {apuracao.isError && <Falha erro={apuracao.error} tentarDeNovo={() => apuracao.refetch()} />}

      {apuracao.isSuccess && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Cartao titulo="Totais do dia" subtitulo="Minutos inteiros, nunca float de horas">
            <dl className="space-y-2.5 text-sm">
              {([
                ["Trabalhado", apuracao.data.trabalhadoMin],
                ["Previsto", apuracao.data.previstoMin],
                ["Atraso", apuracao.data.atrasoMin],
                ["Intervalo gozado", apuracao.data.intervaloGozadoMin],
                ["Intrajornada a indenizar", apuracao.data.intrajornadaIndenizavelMin],
                ["Noturno pelo relógio", apuracao.data.noturnoRelogioMin],
                // A hora noturna vale 52min30s: o ficto é MAIOR que o relógio,
                // e confundir os dois é o erro clássico do adicional noturno.
                ["Noturno ficto", apuracao.data.noturnoFictoMin],
                ["Violação de interjornada", apuracao.data.interjornadaViolacaoMin],
              ] as const).map(([rotulo, minutos]) => (
                <div key={rotulo} className="flex items-baseline justify-between gap-4">
                  <dt>{rotulo}</dt>
                  <dd className="moeda font-medium">{duracao(minutos)}</dd>
                </div>
              ))}
            </dl>

            <h3 className="eyebrow mt-6">Horas extras por faixa</h3>
            {apuracao.data.extraPorFaixa.length === 0 ? (
              <p className="mt-2 text-sm" style={{ color: "var(--muted-foreground)" }}>
                Nenhuma hora extra neste dia.
              </p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {apuracao.data.extraPorFaixa.map((f) => (
                  <li key={f.percentual} className="flex justify-between">
                    <span>Adicional de {f.percentual}%</span>
                    <span className="moeda">{duracao(f.minutos)}</span>
                  </li>
                ))}
              </ul>
            )}

            <p className="carimbo mt-4">
              Regra aplicada: intrajornada {apuracao.data.regraAplicada.vigenciaDaRegra}
            </p>
          </Cartao>

          <Cartao titulo="Inconsistências"
                  subtitulo="O sistema sinaliza, nunca bloqueia a marcação">
            {(apuracao.data.inconsistencias ?? []).length === 0 ? (
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Nenhuma inconsistência neste dia.
              </p>
            ) : (
              <ul className="space-y-2.5 text-sm">
                {apuracao.data.inconsistencias.map((i, n) => (
                  <li key={n} className="flex items-start gap-1.5">
                    <span
                      aria-hidden
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: i.severidade === "bloqueante" ? "#C6402A"
                        : i.severidade === "aviso" ? "#B4780C" : "#5C544A" }}
                    />
                    <span>{i.mensagem}</span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        </div>
      )}
    </Pagina>
  );
}
